import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const RouteFormModal = ({ isOpen, onClose, onSubmit, editRoute = null }) => {
  const [formData, setFormData] = useState({
    route_name: '',
    start_point: '',
    end_point: '',
    departure_times: [],
    is_active: true
  });

  const [newDepartureTime, setNewDepartureTime] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editRoute) {
      setFormData({
        route_name: editRoute?.route_name,
        start_point: editRoute?.start_point,
        end_point: editRoute?.end_point,
        departure_times: editRoute?.departure_times || [],
        is_active: editRoute?.is_active
      });
    } else {
      setFormData({
        route_name: '',
        start_point: '',
        end_point: '',
        departure_times: [],
        is_active: true
      });
    }
    setNewDepartureTime('');
    setErrors({});
  }, [editRoute, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.route_name?.trim()) {
      newErrors.route_name = 'Rota adı zorunludur';
    } else if (formData?.route_name?.trim()?.length < 3) {
      newErrors.route_name = 'Rota adı en az 3 karakter olmalıdır';
    }

    if (!formData?.start_point?.trim()) {
      newErrors.start_point = 'Başlangıç noktası zorunludur';
    }

    if (!formData?.end_point?.trim()) {
      newErrors.end_point = 'Bitiş noktası zorunludur';
    }

    if (formData?.start_point?.trim() && formData?.end_point?.trim() && 
        formData?.start_point?.trim()?.toLowerCase() === formData?.end_point?.trim()?.toLowerCase()) {
      newErrors.end_point = 'Bitiş noktası başlangıç noktasından farklı olmalıdır';
    }

    if (formData?.departure_times?.length === 0) {
      newErrors.departure_times = 'En az bir kalkış saati eklemelisiniz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit({
        ...formData,
        id: editRoute?.id || Date.now(),
        last_modified: new Date()?.toISOString()
      });
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddDepartureTime = () => {
    if (newDepartureTime && !formData?.departure_times?.includes(newDepartureTime)) {
      const updatedTimes = [...formData?.departure_times, newDepartureTime]?.sort();
      setFormData(prev => ({ ...prev, departure_times: updatedTimes }));
      setNewDepartureTime('');
      if (errors?.departure_times) {
        setErrors(prev => ({ ...prev, departure_times: '' }));
      }
    }
  };

  const handleRemoveDepartureTime = (time) => {
    setFormData(prev => ({
      ...prev,
      departure_times: prev?.departure_times?.filter(t => t !== time)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-2000 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-card rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
          <h2 className="text-xl font-bold text-foreground font-heading">
            {editRoute ? 'Rota Düzenle' : 'Yeni Rota Ekle'}
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Kapat"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Rota Adı"
            type="text"
            placeholder="Örn: Meram - Karatay"
            value={formData?.route_name}
            onChange={(e) => handleChange('route_name', e?.target?.value)}
            error={errors?.route_name}
            required
          />

          <Input
            label="Başlangıç Noktası"
            type="text"
            placeholder="Örn: Meram Meydanı"
            value={formData?.start_point}
            onChange={(e) => handleChange('start_point', e?.target?.value)}
            error={errors?.start_point}
            required
          />

          <Input
            label="Bitiş Noktası"
            type="text"
            placeholder="Örn: Karatay Meydanı"
            value={formData?.end_point}
            onChange={(e) => handleChange('end_point', e?.target?.value)}
            error={errors?.end_point}
            required
          />

          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Kalkış Saatleri <span className="text-error">*</span>
            </label>
            <div className="flex gap-2">
              <Input
                type="time"
                placeholder="Saat seçin"
                value={newDepartureTime}
                onChange={(e) => setNewDepartureTime(e?.target?.value)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddDepartureTime}
                iconName="Plus"
                disabled={!newDepartureTime}
              >
                Ekle
              </Button>
            </div>
            
            {formData?.departure_times?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {formData?.departure_times?.map((time, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm font-medium text-foreground"
                  >
                    <Icon name="Clock" size={14} color="var(--color-primary)" />
                    <span>{time}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveDepartureTime(time)}
                      className="ml-1 hover:text-error transition-smooth"
                      aria-label="Sil"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {errors?.departure_times && (
              <p className="text-sm text-error">{errors?.departure_times}</p>
            )}
          </div>

          <Checkbox
            label="Rota Aktif"
            description="Aktif rotalar kullanıcılara gösterilir"
            checked={formData?.is_active}
            onChange={(e) => handleChange('is_active', e?.target?.checked)}
          />

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName={editRoute ? "Save" : "Plus"}
              iconPosition="left"
            >
              {editRoute ? 'Güncelle' : 'Ekle'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RouteFormModal;